import * as React from 'react'
import { ExternalLink, Store } from 'lucide-react'
import { useOwnVendor, useRecommendVendor, useVendorProfile, useWithdrawRecommendation } from '@/hooks/useVendors'
import { useAuth } from '@/auth/AuthContext'
import { can } from '@/auth/access'
import { isVendor } from '@/auth/roles'
import type { RecommendAs } from '@/services/VendorService'
import { AskForQuoteButton } from '@/components/quotes/QuoteRequestDialog'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Facts } from '@/components/ui/facts'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { MoreReviews, ReviewItem, ReviewSummary } from '@/components/reviews/ShownReviews'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { VENDOR_SERVICES, VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'
import { VENDOR_OFFERING_KIND_LABELS } from '@lib/enums/vendor-offering-kind.enum'
import { VENDOR_MEDIA_KIND_LABELS } from '@lib/enums/vendor-media-kind.enum'
import { objectPositionOf } from '@lib/helpers/focal-point'
import { offeringDetailLines, offeringPriceLabel } from '@lib/helpers/vendor-offering'
import type { VendorContactDto, VendorOfferingDto, VendorProfileDto } from '@lib/dto/vendor-profile.dto'

const linkClass = 'text-ink underline underline-offset-2 hover:text-ink-muted'

/** An outside address, opened in a new tab: the profile's links are the vendor's own sites. */
function Outside({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className={linkClass}>
      {children}
    </a>
  )
}

/** Where it is and how far it goes: "Austin, TX · travels 60 miles". */
function areaLine(profile: VendorProfileDto): string | null {
  const place = [profile.area.city, profile.area.state].filter((part) => part !== null && part !== '').join(', ')
  const reach = profile.area.serviceRadiusMiles === null ? null : `travels ${String(profile.area.serviceRadiusMiles)} miles`
  const line = [place === '' ? null : place, reach].filter((part) => part !== null).join(' · ')
  return line === '' ? null : line
}

function socialLinks(contact: VendorContactDto): React.ReactNode {
  const links = [
    contact.instagramUrl === null ? null : { href: contact.instagramUrl, label: 'Instagram' },
    contact.facebookUrl === null ? null : { href: contact.facebookUrl, label: 'Facebook' },
    contact.tiktokUrl === null ? null : { href: contact.tiktokUrl, label: 'TikTok' },
    contact.pinterestUrl === null ? null : { href: contact.pinterestUrl, label: 'Pinterest' },
  ].filter((link) => link !== null)
  if (links.length === 0) return null
  return (
    <span className="flex flex-wrap gap-x-3">
      {links.map((link) => (
        <Outside key={link.label} href={link.href}>
          {link.label}
        </Outside>
      ))}
    </span>
  )
}

function OfferingCard({ offering }: { offering: VendorOfferingDto }) {
  const lines = offeringDetailLines(offering)
  return (
    <Card asChild className="space-y-2 p-4">
      <li>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium text-ink">
            {offering.name} <Badge className="ml-1 align-middle">{VENDOR_OFFERING_KIND_LABELS[offering.kind]}</Badge>
          </span>
          <span className="text-sm text-ink">{offeringPriceLabel(offering.price)}</span>
        </div>
        {offering.description === '' ? null : <p className="m-0 whitespace-pre-line text-sm text-ink-muted">{offering.description}</p>}
        {lines.length === 0 ? null : (
          <ul className="m-0 list-none space-y-0.5 p-0 text-sm text-ink-muted">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {offering.photos.length === 0 ? null : (
          <div className="flex flex-wrap gap-2">
            {offering.photos.map((photo) => (
              <img key={photo.url} src={photo.url} alt={photo.caption ?? ''} className="h-16 w-20 rounded-md border border-line object-cover" loading="lazy" />
            ))}
          </div>
        )}
      </li>
    </Card>
  )
}

function ProfileBody({ profile }: { profile: VendorProfileDto }) {
  const [viewing, setViewing] = React.useState<VendorProfileDto['photos'][number] | null>(null)
  const services = VENDOR_SERVICES.filter((service) => profile.offerings.some((o) => o.service === service))

  return (
    <div className="space-y-6">
      {profile.coverImageUrl === null ? null : (
        <img src={profile.coverImageUrl} alt="" className="aspect-[4/3] w-full rounded-lg object-cover" />
      )}

      <section className="space-y-1.5">
        <h3 className="m-0 text-sm font-semibold text-ink">About</h3>
        {profile.description === null ? (
          <p className="m-0 text-sm text-ink-muted">No description yet.</p>
        ) : (
          <p className="m-0 whitespace-pre-line text-sm text-ink">{profile.description}</p>
        )}
      </section>

      <section className="space-y-1.5">
        <h3 className="m-0 text-sm font-semibold text-ink">Contact</h3>
        <Facts
          layout="stack"
          facts={[
            {
              label: 'Website',
              value: profile.contact.website === null ? null : <Outside href={profile.contact.website}>{profile.contact.website}</Outside>,
              empty: 'Not given',
            },
            {
              label: 'Phone',
              value: profile.contact.phone === null ? null : <a href={`tel:${profile.contact.phone}`} className={linkClass}>{profile.contact.phone}</a>,
              empty: 'Not given',
            },
            {
              label: 'Email',
              value:
                profile.contact.publicEmail === null ? null : (
                  <a href={`mailto:${profile.contact.publicEmail}`} className={linkClass}>
                    {profile.contact.publicEmail}
                  </a>
                ),
              empty: 'Not given',
            },
            { label: 'Social', value: socialLinks(profile.contact), empty: 'Not given' },
          ]}
        />
      </section>

      <section className="space-y-1.5">
        <h3 className="m-0 text-sm font-semibold text-ink">Photos</h3>
        {profile.photos.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">No photos yet.</p>
        ) : (
          <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3">
            {profile.photos.map((photo) => (
              <li key={photo.id}>
                <button type="button" className="block w-full" onClick={() => setViewing(photo)} aria-label={photo.caption ?? 'Open this photo'}>
                  <img
                    src={photo.url}
                    alt={photo.caption ?? ''}
                    className="aspect-square w-full rounded-md object-cover"
                    style={{ objectPosition: objectPositionOf(photo.focalPoint) }}
                    loading="lazy"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {profile.mediaLinks.length === 0 ? null : (
        <section className="space-y-1.5">
          <h3 className="m-0 text-sm font-semibold text-ink">Videos and audio</h3>
          <ul className="m-0 list-none space-y-1.5 p-0">
            {profile.mediaLinks.map((link) => (
              <li key={link.id} className="flex items-center gap-2 text-sm">
                <Badge tone="info">{VENDOR_MEDIA_KIND_LABELS[link.kind]}</Badge>
                <Outside href={link.url}>
                  {link.title ?? link.url} <ExternalLink className="inline h-3 w-3" aria-hidden />
                </Outside>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <h3 className="m-0 text-sm font-semibold text-ink">What they offer</h3>
        {services.length === 0 ? <p className="m-0 text-sm text-ink-muted">Nothing listed yet.</p> : null}
        {services.map((service) => (
          <div key={service} className="space-y-2">
            <p className="m-0 text-xs font-medium uppercase tracking-wide text-ink-muted">{VENDOR_SERVICE_LABELS[service]}</p>
            <ul className="m-0 list-none space-y-2 p-0">
              {profile.offerings
                .filter((offering) => offering.service === service)
                .map((offering) => (
                  <OfferingCard key={offering.id} offering={offering} />
                ))}
            </ul>
          </div>
        ))}
      </section>

      {/* What couples and planners said, once FLOH Venues approved it (docs/vendor-growth.spec.md, "Reviews"). */}
      {profile.reviews.summary === null ? null : (
        <section className="space-y-3">
          <h3 className="m-0 text-sm font-semibold text-ink">Reviews</h3>
          <ReviewSummary summary={profile.reviews.summary} />
          <ul className="m-0 list-none space-y-4 p-0">
            {profile.reviews.reviews.map((review) => (
              <ReviewItem key={review.id} review={review} replyFrom={profile.name} />
            ))}
          </ul>
          <MoreReviews shown={profile.reviews.reviews.length} summary={profile.reviews.summary} />
        </section>
      )}

      <Dialog open={viewing !== null} onOpenChange={(open) => (open ? undefined : setViewing(null))}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="sr-only">{viewing?.caption ?? 'Photo'}</DialogTitle>
          {viewing === null ? null : <img src={viewing.url} alt={viewing.caption ?? ''} className="max-h-[80vh] w-full rounded-md object-contain" />}
          {viewing?.caption ? <p className="m-0 text-sm text-ink-muted">{viewing.caption}</p> : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Where a profile was opened from, so the sheet can offer what is done there. */
export interface ProfileContext {
  /**
   * Opened from a venue's Settings > Vendors: its staff may recommend the
   * company there while the venue works with it. `recommended` is the list's
   * own word on it, refreshed when it changes.
   */
  venue?: { id: string; name: string; worksWith: boolean; recommended: boolean }
  /** Opened from an event: whether the reader may ask the company for a quote for it. */
  event?: { id: string; canAskForQuote: boolean }
}

/** Who may recommend the company from here, in whose name, and whether they already do. */
interface Recommender {
  by: RecommendAs
  name: string
  recommended: boolean
}

/**
 * The venue's staff with Vendors Edit, opened from its Settings > Vendors; or a
 * planning company, wherever it reads another company's profile. Nobody else.
 */
function useRecommender(profile: VendorProfileDto, context: ProfileContext | undefined): Recommender | null {
  const { user } = useAuth()
  const own = useOwnVendor({ enabled: isVendor(user) })
  const venue = context?.venue
  if (venue?.worksWith && can(user, 'vendors:edit')) {
    return { by: { as: 'venue', venueId: venue.id }, name: venue.name, recommended: venue.recommended }
  }
  if (isVendor(user) && own.data?.services.includes('planning') && profile.vendorId !== user.vendorId) {
    return { by: { as: 'planner' }, name: own.data.name, recommended: profile.recommendedByYou }
  }
  return null
}

/**
 * Recommending the company by name (docs/vendor-growth.spec.md, "Reputation"),
 * said before it is done: the name goes on the profile for others to see.
 */
function RecommendButton({ profile, context }: { profile: VendorProfileDto; context: ProfileContext | undefined }) {
  const recommender = useRecommender(profile, context)
  const recommend = useRecommendVendor()
  const withdraw = useWithdrawRecommendation()
  if (recommender === null) return null
  const busy = recommend.isPending || withdraw.isPending
  const failed = recommend.error ?? withdraw.error
  const target = { by: recommender.by, vendorId: profile.vendorId }

  return (
    <div className="mb-6 space-y-1.5">
      {recommender.recommended ? (
        <div className="flex items-center gap-2">
          <Badge tone="sage">Recommended</Badge>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => withdraw.mutate(target)}>
            Take back
          </Button>
        </div>
      ) : (
        <>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => recommend.mutate(target)}>
            Recommend
          </Button>
          <p className="m-0 text-xs text-ink-muted">
            &ldquo;Recommended by {recommender.name}&rdquo; will show on this company&rsquo;s profile to venues, planners and couples,
            and on its public page when it has one. You can take it back at any time.
          </p>
        </>
      )}
      {failed ? <FormError size="sm">{requestErrorMessage(failed)}</FormError> : null}
    </div>
  )
}

interface Props {
  /** The company to show; null while closed. */
  vendorId: string | null
  /** Its name, for the header while the profile loads. */
  name: string
  onClose: () => void
  /** Where it was opened from. */
  context?: ProfileContext | undefined
}

/**
 * A vendor company's profile, as venue staff and planners read it when they
 * choose vendors (docs/vendor-growth.spec.md): its logo, description, how to
 * reach it, its work, and what it offers at what price. Opened from wherever a
 * company is named, through `VendorProfileLink`.
 */
export function VendorProfileSheet({ vendorId, name, onClose, context }: Props) {
  const profile = useVendorProfile(vendorId)
  const notFound = profile.isError && isApiError(profile.error) && profile.error.status === 404

  return (
    <Sheet open={vendorId !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent side="right" className="max-w-2xl">
        <SheetHeader>
          <div className="flex items-center gap-3 pr-8">
            {profile.data?.logoUrl ? (
              <img src={profile.data.logoUrl} alt="" className="h-14 w-14 shrink-0 rounded-md border border-line bg-surface object-contain p-1" />
            ) : (
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-surface-sunken">
                <Store className="h-6 w-6 text-ink-muted" aria-hidden />
              </span>
            )}
            <div className="min-w-0">
              <SheetTitle>{profile.data?.name ?? name}</SheetTitle>
              <SheetDescription>
                {profile.data === undefined
                  ? ' '
                  : [profile.data.services.map((s) => VENDOR_SERVICE_LABELS[s]).join(' · '), areaLine(profile.data)].filter((part) => part !== null && part !== '').join(' — ')}
              </SheetDescription>
              {profile.data && profile.data.recommendations.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {/* By place: two venues may share a name. */}
                  {profile.data.recommendations.map((r, i) => (
                    <Badge key={i} tone="sage">
                      Recommended by {r.name}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {profile.isPending && vendorId !== null ? <LoadingState label="Loading the profile…" /> : null}
          {notFound ? <p className="text-sm text-ink-muted">This company’s profile isn’t available.</p> : null}
          {profile.isError && !notFound ? <ErrorState message={requestErrorMessage(profile.error)} onRetry={() => void profile.refetch()} /> : null}
          {profile.data && context?.event?.canAskForQuote ? (
            <div className="mb-6">
              <AskForQuoteButton eventId={context.event.id} vendor={{ id: profile.data.vendorId, name: profile.data.name }} variant="default" />
            </div>
          ) : null}
          {profile.data ? <RecommendButton profile={profile.data} context={context} /> : null}
          {profile.data ? <ProfileBody profile={profile.data} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}
